// src/pages/Shortansedit.jsx
import React, { useState, useRef, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import "../styles/Shortans.css";
import { FaBell, FaUserCircle } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useLocation, useParams } from "react-router-dom";

const apiBase = "https://digital.tifleducation.com/admin/public/api";
const LOCAL_SELECTED_KEY = "selectedQuestionsByChapter"; // unified key for all types

const Shortansedit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paperId } = useParams();
  const state = location.state || {};

  const [activeItem, setActiveItem] = useState("papers");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [user, setUser] = useState({ id: null, name: "", email: "", password: "" });
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [shortLimit, setShortLimit] = useState(0);

  const boardId = state.boardId || localStorage.getItem("selectedBoardId");
  const gradeId = state.gradeId || localStorage.getItem("selectedGradeId");
  const subjectId = state.subjectId || localStorage.getItem("selectedSubjectId");
  const initialShortLimit = state.shortLimit || Number(JSON.parse(localStorage.getItem("regularPaperForm") || "{}").short || 0);

  const toggleDropdown = () => setShowDropdown(prev => !prev);

  // -------------------- FETCH USER --------------------
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${apiBase}/getDashboard`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.user) {
          setUser({ id: res.data.user.id, name: res.data.user.name || "", email: res.data.user.email || "", password: "" });
        }
      } catch (error) {
        console.error("Error fetching user data:", error.response?.data || error);
      }
    };
    fetchUserData();
  }, []);

  // -------------------- FETCH SHORT QUESTIONS --------------------
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!boardId || !gradeId || !subjectId) {
        toast.error("Missing selected board/grade/subject.");
        return;
      }

      setShortLimit(initialShortLimit);

      try {
        const token = localStorage.getItem("token");
        let data = {};

        if (paperId && paperId !== "new") {
          const res = await axios.get(`${apiBase}/papers/${paperId}`, { headers: { Authorization: `Bearer ${token}` } });
          data = res.data?.questions || {};
        } else {
          const res = await axios.post(`${apiBase}/papers/fetch-questions`,
            { board: Number(boardId), grade: Number(gradeId), subject: Number(subjectId) },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          data = res.data || {};
        }

        // Flatten short questions
        const shortQs = [];
        Object.entries(data).forEach(([chapterId, chapterData]) => {
          const types = chapterData.types || {};
          const arr = types["Short Question Answer"] || [];
          (Array.isArray(arr) ? arr : []).forEach(q => shortQs.push({ ...q, chapter_id: q.chapter_id ?? Number(chapterId) }));
        });
        setQuestions(shortQs);

        // Restore selected questions
        const savedByChapter = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || "{}");
        const selectedMap = {};
        Object.values(savedByChapter || {}).forEach(chapterData => {
          const arr = chapterData?.short_qa || [];
          (Array.isArray(arr) ? arr : []).forEach(id => { selectedMap[Number(id)] = true; });
        });
        setSelectedQuestions(selectedMap);

      } catch (error) {
        console.error("Error fetching short questions:", error.response?.data || error);
        toast.error("Failed to fetch questions.");
      }
    };

    fetchQuestions();
  }, [boardId, gradeId, subjectId, paperId]);

  // -------------------- HANDLE QUESTION SELECTION --------------------
  const handleSelectQuestion = async (qId, chapterId) => {
    const alreadySelected = !!selectedQuestions[qId];
    const currentCount = Object.keys(selectedQuestions).length;

    if (!shortLimit) return toast.error("No Short Question limit set.");
    if (!alreadySelected && currentCount >= shortLimit) return toast.error(`You can only select up to ${shortLimit} Short Questions.`);

    setSelectedQuestions(prev => {
      const next = { ...prev };
      if (next[qId]) delete next[qId];
      else next[qId] = true;
      return next;
    });

    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || "{}");
      const key = String(chapterId);
      if (!saved[key]) saved[key] = { mcq: [], fill_in_the_blanks: [], true_false: [], very_short_qa: [], short_qa: [], long_qa: [], matching_type: [], diagram_image_based: [] };
      const arr = saved[key].short_qa || [];
      const idx = arr.findIndex(x => Number(x) === Number(qId));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(Number(qId));
      saved[key].short_qa = arr;
      localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(saved));

      if (paperId && paperId !== "new") {
        const token = localStorage.getItem("token");
        await axios.put(`${apiBase}/papers/${paperId}`, { selectedQuestions: saved }, { headers: { Authorization: `Bearer ${token}` } });
      }

      toast.success("Selection saved");
    } catch (error) {
      console.error("Error saving selection:", error.response?.data || error);
      toast.error("Failed to save selection");
    }
  };

  // -------------------- SAVE PROFILE --------------------
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${apiBase}/update-profile`, { name: user.name, password: user.password }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: "" });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  // -------------------- OUTSIDE CLICK --------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  return (
    <div className="dashboard-container">
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} activeItem={activeItem} setActiveItem={setActiveItem} />

      <main className="main-content">
        <div className="short-header">
          <div className="short-welcome-text">
            <a onClick={() => navigate(-1)} className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="short-welcome-text">Short Questions</span>
          </div>

          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className="edituser" onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="short-questions-container">
          {questions.length ? questions.map((q, idx) => (
            <div key={q.id} className="short-question-box">
              <div className="short-question">
                <input
                  type="checkbox"
                  className="short-checkbox"
                  checked={!!selectedQuestions[q.id]}
                  onChange={() => handleSelectQuestion(q.id, q.chapter_id)}
                />
                <span className="question-text" dangerouslySetInnerHTML={{ __html: `${idx + 1}. ${String(q.question || q.question_title || "").replace(/<\/?p>/g, "")}` }} />
              </div>
            </div>
          )) : (
            <div className="no-questions-container">
              <div className="no-questions-icon">📄</div>
              <h3>No Short Questions Found</h3>
              <p style={{ color: "#666" }}>There are currently no short questions for this Board / Grade / Subject.</p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={e => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: "#f0f0f0", color: "#888" }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: "right" }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default Shortansedit;
